import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

export interface FilterUrlSyncConfig<F extends object> {
  parseFromSearchParams: (params: URLSearchParams) => F;
  applyToSearchParams: (filter: F, current: URLSearchParams) => URLSearchParams;
  filtersEquivalent: (a: F, b: F) => boolean;
  clearedFilter: Partial<F>;
}

/**
 * Keeps a list filter and the address-bar query string in lockstep.
 * Writes use `replace`; reads only when the query string itself changes.
 */
export function useFilterUrlSync<F extends object>(
  filter: F,
  setFilter: (next: Partial<F>) => void,
  config: FilterUrlSyncConfig<F>,
  options?: {
    /** Called when the URL changes and carries a new `search` value. */
    syncSearchInput?: (search: string) => void;
  },
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const configRef = useRef(config);
  configRef.current = config;
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const syncSearchRef = useRef(options?.syncSearchInput);
  syncSearchRef.current = options?.syncSearchInput;

  const searchParamsKey = searchParams.toString();
  const prevSearchParamsKey = useRef(searchParamsKey);
  const initialSyncPending = useRef(true);
  useEffect(() => {
    const {
      parseFromSearchParams,
      applyToSearchParams,
      filtersEquivalent,
      clearedFilter,
    } = configRef.current;

    // A remounted list can receive a stale one-time initialFilter while the URL
    // already contains a newly applied saved filter. Hydrate URL-owned fields
    // before allowing list state to write back, preserving all non-URL fields
    // because setFilter merges this partial value into the resource filter.
    if (initialSyncPending.current) {
      const fromUrl = parseFromSearchParams(searchParams);
      const mergedNextFilter = { ...clearedFilter, ...fromUrl };
      if (!filtersEquivalent(mergedNextFilter, filter)) {
        syncSearchRef.current?.((fromUrl as { search?: string }).search ?? "");
        setFilter(mergedNextFilter);
        // Keep URL hydration pending until React renders the matching filter.
        // Otherwise an intervening effect can write stale list state to the URL.
        return;
      }
      initialSyncPending.current = false;
      return;
    }

    // URL changes (for example, applying a saved filter) take precedence over
    // the filter still held by the list. Keeping URL reads and writes in one
    // effect prevents the old list filter and the saved URL filter from
    // repeatedly overwriting each other.
    if (prevSearchParamsKey.current !== searchParamsKey) {
      prevSearchParamsKey.current = searchParamsKey;
      const fromUrl = parseFromSearchParams(searchParams);
      if (filtersEquivalent(fromUrl, filter)) return;
      syncSearchRef.current?.((fromUrl as { search?: string }).search ?? "");
      const mergedNextFilter = { ...clearedFilter, ...fromUrl };
      if (!filtersEquivalent(mergedNextFilter, filter)) {
        setFilter(mergedNextFilter);
      }
      return;
    }

    const current = searchParamsRef.current;
    if (filtersEquivalent(parseFromSearchParams(current), filter)) return;
    const next = applyToSearchParams(filter, current);
    const nextKey = next.toString();
    if (nextKey === searchParamsKey) return;
    // Record our own write now so the following render is not mistaken for an
    // external URL change that needs to be applied back to list state.
    prevSearchParamsKey.current = nextKey;
    setSearchParams(next, { replace: true });
  }, [searchParamsKey, searchParams, filter, setFilter, setSearchParams]);
}

/** Mount when a page opts into URL sync so callers avoid a no-op hook. */
export function FilterUrlSyncEffect<F extends object>({
  filter,
  setFilter,
  config,
  syncSearchInput,
}: {
  filter: F;
  setFilter: (next: Partial<F>) => void;
  config: FilterUrlSyncConfig<F>;
  syncSearchInput?: (search: string) => void;
}) {
  useFilterUrlSync(filter, setFilter, config, { syncSearchInput });
  return null;
}
